/**
 * GatewayHealthDetail — full health metrics view.
 * Sections: System, Network, Bus, Buffer, OTA, Commands.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { GatewayDevice } from '@/types/gateway';
import { getWifiQuality, formatUptime } from '@/types/gateway';

interface GatewayHealthDetailProps {
  device: GatewayDevice | null;
  isLoading: boolean;
}

function MetricRow({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-wt-text-secondary">{label}</span>
      <span className={`text-sm font-mono-numeric ${valueClass ?? 'text-wt-text-primary'}`}>{value}</span>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-1">
        <CardTitle className="text-xs font-medium text-wt-text-secondary uppercase tracking-wider">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-0">{children}</CardContent>
    </Card>
  );
}

export function GatewayHealthDetail({ device, isLoading }: GatewayHealthDetailProps) {
  if (isLoading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="py-8 text-center text-sm text-wt-text-secondary">
        No health data available
      </div>
    );
  }

  if (!device.last_heartbeat_at) {
    return (
      <div className="py-8 text-center text-sm text-wt-text-secondary">
        No health data yet. The device has not sent a heartbeat.
      </div>
    );
  }

  const wifiQ = getWifiQuality(device.wifi_rssi);
  const busErrors = device.bus_errors_by_device ?? {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SectionCard title="System">
        <MetricRow label="Free Heap" value={device.free_heap != null ? `${(device.free_heap / 1024).toFixed(0)} KB` : '--'} />
        <MetricRow label="Free PSRAM" value={device.free_psram != null ? `${(device.free_psram / 1024).toFixed(0)} KB` : '--'} />
        <MetricRow label="Uptime" value={formatUptime(device.uptime_seconds)} />
        <MetricRow label="Boot Count" value={String(device.boot_count ?? '--')} />
      </SectionCard>

      <SectionCard title="Network">
        <MetricRow label="WiFi RSSI" value={device.wifi_rssi != null ? `${device.wifi_rssi} dBm` : '--'} valueClass={wifiQ.color} />
        <MetricRow label="WiFi Quality" value={wifiQ.label} valueClass={wifiQ.color} />
        <MetricRow label="NTP Synced" value={device.ntp_synced ? 'Yes' : 'No'} valueClass={device.ntp_synced ? 'text-wt-green-primary' : 'text-wt-amber-primary'} />
      </SectionCard>

      <SectionCard title="Bus">
        <MetricRow
          label="Devices"
          value={`${device.bus_devices_responding ?? 0}/${device.bus_devices_expected ?? '?'}`}
          valueClass={
            device.bus_devices_responding != null &&
            device.bus_devices_expected != null &&
            device.bus_devices_responding < device.bus_devices_expected
              ? 'text-wt-amber-primary'
              : undefined
          }
        />
        <MetricRow label="Total Errors" value={String(device.bus_errors_total ?? 0)} />
        <MetricRow
          label="Last Poll"
          value={device.last_successful_poll ? formatDistanceToNow(new Date(device.last_successful_poll), { addSuffix: true }) : 'Never'}
        />
        {Object.keys(busErrors).length > 0 && (
          <div className="pt-2">
            <p className="text-xs text-wt-text-secondary mb-1">Errors by Device</p>
            <div className="space-y-0.5">
              {Object.entries(busErrors).map(([addr, count]) => (
                <div key={addr} className="flex items-center justify-between text-xs">
                  <span className="font-mono-numeric text-wt-text-secondary">Addr {addr}</span>
                  <span className={`font-mono-numeric ${Number(count) > 5 ? 'text-wt-red-primary' : 'text-wt-text-primary'}`}>
                    {String(count)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Buffer">
        <MetricRow label="Pending Records" value={String(device.pending_buffer_records ?? 0)} />
        <MetricRow label="Overflow Count" value={String(device.buffer_overflow_count ?? 0)} />
        <MetricRow
          label="Last Overflow"
          value={device.buffer_last_overflow_at ? formatDistanceToNow(new Date(device.buffer_last_overflow_at), { addSuffix: true }) : 'Never'}
        />
        <MetricRow
          label="Last Upload"
          value={device.last_successful_upload ? formatDistanceToNow(new Date(device.last_successful_upload), { addSuffix: true }) : 'Never'}
        />
      </SectionCard>

      <SectionCard title="OTA">
        <MetricRow label="Channel" value={device.ota_channel} />
        <MetricRow label="Firmware" value={device.firmware_version ?? '--'} />
      </SectionCard>

      <SectionCard title="Configuration">
        <MetricRow label="Poll Interval" value={`${device.poll_interval_ms / 1000}s`} />
        <MetricRow label="Upload Interval" value={`${device.upload_interval_ms / 1000}s`} />
        <MetricRow label="Heartbeat Interval" value={`${device.heartbeat_interval_ms / 1000}s`} />
        <MetricRow label="Command Poll" value={`${device.command_poll_interval_ms / 1000}s`} />
        <MetricRow label="Diagnostic Mode" value={device.diagnostic_mode ? 'Active' : 'Off'} valueClass={device.diagnostic_mode ? 'text-wt-amber-primary' : undefined} />
        <MetricRow label="Writes Enabled" value={device.writes_enabled ? 'Yes' : 'No'} valueClass={device.writes_enabled ? 'text-wt-green-primary' : undefined} />
      </SectionCard>
    </div>
  );
}
